import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Navbar.css';

const navConfig = {
    '/dashboard': {
        title: 'Dashboard',
        addLabel: 'Add Widget',
        addAction: null,
        showSearch: true,
    },
    '/users': {
        title: 'Users',
        addLabel: 'Add User',
        addAction: null,
        showSearch: false,
    },
    '/subscription': {
        title: 'Subscription',
        addLabel: 'Add Subscription',
        addAction: '/subscription',
        showSearch: true,
    },
    '/notifications': {
        title: 'Push Notification',
        addLabel: 'Send Notification',
        addAction: null,
        showSearch: false,
    },
    '/profile': {
        title: 'Admin Profile',
        addLabel: 'Edit Profile',
        addAction: null,
        showSearch: false,
    },
};

// ── Avatar Dropdown ──────────────────────────────────────────────
function AvatarDropdown({ adminName, adminEmail, adminRole, adminImage }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();
   

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = () => {
        setOpen(false);
        // Clear session if needed
        window.location.href = '/';
    };

    const initials = adminName
        ? adminName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
        : 'A';

    return (
        <div className="avatar-wrapper" ref={ref}>
            {/* Avatar Button */}
            <button
                className="navbar-avatar-btn"
                onClick={() => setOpen((v) => !v)}
                aria-label="Open profile menu"
                aria-expanded={open}
            >
                {adminImage ? (
                    <img src={adminImage} alt={adminName} className="navbar-avatar-img" />
                ) : (
                    <span className="navbar-avatar-initials">{initials}</span>
                )}
                {/* <span className={`avatar-online-dot ${open ? 'active' : ''}`} /> */}
            </button>

            {/* Dropdown */}
            {open && (
                <div className="avatar-dropdown" role="menu">
                    {/* Profile Header */}
                    <div className="dropdown-profile-header">
                        <div className="dropdown-avatar-wrap">
                            {adminImage ? (
                                <img src={adminImage} alt={adminName} className="dropdown-avatar-img" />
                            ) : (
                                <div className="dropdown-avatar-placeholder">{initials}</div>
                            )}
                        </div>
                        <div className="dropdown-profile-info">
                            <span className="dropdown-profile-name">{adminName || 'Vegas Karts'}</span>
                            <span className="dropdown-profile-email">{adminEmail || 'admin@vegas.com'}</span>
                        </div>
                    </div>

                    <div className="dropdown-divider" />

                    {/* View Profile */}
                    <Link
                        to="/profile"
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                    >
                        <span className="dropdown-item-icon">
                            <User size={15} />
                        </span>
                        View Profile
                    </Link>

                    {/* Logout */}
                    <button
                        className="dropdown-item dropdown-item-danger"
                        role="menuitem"
                        onClick={handleLogout}
                    >
                        <span className="dropdown-item-icon">
                            <LogOut size={15} />
                        </span>
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Navbar ───────────────────────────────────────────────────────
function Navbar({
    onMobileMenuToggle,
    mobileOpen,
    sidebarCollapsed,
    searchQuery,
    setSearchQuery,
    adminName,
    adminEmail,
    adminRole,
    adminImage,
}) {
    const location = useLocation();
    const config = navConfig[location.pathname] || { title: 'Admin', addLabel: 'Add', showSearch: true };
     const { admin } = useSelector((state) => state.admin);
    const dispatch = useDispatch();

    return (
        <header className={`navbar ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Left */}
            <div className="navbar-left">
                <button
                    className="mobile-menu-btn"
                    onClick={onMobileMenuToggle}
                    aria-label="Toggle menu"
                >
                    {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                </button>

                <div className="navbar-breadcrumb">
                    <span className="breadcrumb-home">Admin</span>
                    <span className="breadcrumb-sep">/</span>
                    <span className="breadcrumb-current">{config.title}</span>
                </div>
            </div>

            {/* Search */}
            {config.showSearch && (
                <div className="navbar-search">
                    <Search size={15} className="search-icon" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search ${config.title.toLowerCase()}...`}
                        className="search-input"
                    />
                </div>
            )}

            {/* Right */}
            <div className="navbar-right">
                <AvatarDropdown
                    adminName={admin?.name || "Vegas Karts"}
                    adminEmail={admin?.email || "admin@vegas.com"}
                    adminRole={admin?.role || "Super Admin"}
                    adminImage={admin?.profilePic || "/images/profile.jpg"}
                />
            </div>
        </header>
    );
}

export default Navbar;